import { FC, useState } from 'react';
import { ChevronsRight, ChevronsLeft, Menu, X } from 'lucide-react';
import { cn } from '@new/lib';
import { Can } from '@new/casl';
import { ICON_SIZES, ICON_STROKE_WIDTH } from './constants';
import logo from '@assets/logo.svg';
import logoText from '@assets/newlane-university.svg';
import { SidebarItem } from './SidebarItem';
import { SIDEBAR_ITEMS } from './sidebarConfig';

export type { SidebarConfigItem } from './sidebarConfig';

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

export const Sidebar: FC<SidebarProps> = ({ collapsed, onToggle }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const ToggleIcon = collapsed ? ChevronsRight : ChevronsLeft;

  const handleDesktopToggle = () => {
    onToggle();
  };

  const handleMobileToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const closeMobileSidebar = () => {
    setMobileOpen(false);
  };

  return (
    <>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-(--neutral-black-NB1100)/50 z-9 lg:hidden"
          onClick={closeMobileSidebar}
        />
      )}

      {/* Mobile toggle button with logo - only visible when sidebar is closed */}
      {!mobileOpen && (
        <button
          onClick={handleMobileToggle}
          className="fixed top-7 left-5 z-30 lg:hidden flex items-center gap-2 bg-(--neutral-white-NW1000) rounded-lg hover:bg-(--neutral-black-NB100) transition-colors"
          aria-label="Open menu"
        >
          <Menu size={26} className="text-(--neutral-black-NB700)" />
        </button>
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed lg:relative h-screen inline-flex flex-col gap-5 p-5 px-6 z-10',
          'bg-(--neutral-white-NW1000)',
          'transition-all duration-300 ease-in-out',
          // Mobile: slide in/out from left
          'lg:translate-x-0',
          mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
          // Desktop: collapse behavior
          collapsed ? 'max-w-20' : 'w-70 max-w-90'
        )}
      >
        {/* Mobile close button - positioned at top-right of sidebar */}
        <button
          onClick={closeMobileSidebar}
          className="lg:hidden absolute right-0 top-0 p-3 hover:bg-(--neutral-black-NB100) rounded-lg transition-colors z-10"
          aria-label="Close menu"
        >
          <X size={20} className="text-(--neutral-black-NB700)" />
        </button>

        <div className="relative flex items-center">
          <img
            src={logo}
            alt="Logo icon"
            className={cn(
              'transition-all duration-300 ease-in-out h-9',
              collapsed ? 'opacity-100 visible' : 'opacity-0 hidden'
            )}
          />
          <img
            src={logoText}
            alt="Logo text"
            className={cn(
              'overflow-hidden transition-all duration-300 ease-in-out h-9',
              collapsed ? 'max-w-0 opacity-0 hidden' : 'opacity-100 visible'
            )}
          />

          {/* Desktop toggle button */}
          <button
            onClick={handleDesktopToggle}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-expanded={!collapsed}
            className={cn(
              'hidden lg:flex absolute -right-8.75 top-[1/2]',
              'h-6 w-6 items-center justify-center',
              'text-(--neutral-black-NB600) bg-(--neutral-white-NW1000)',
              'cursor-pointer'
            )}
          >
            <ToggleIcon
              strokeWidth={ICON_STROKE_WIDTH}
              size={ICON_SIZES.TOGGLE}
              className="stroke-(--neutral-black-NB600) "
            />
          </button>
        </div>

        <div className="overflow-y-auto flex flex-col gap-2 lg:gap-3 flex-1 min-h-0">
          {SIDEBAR_ITEMS.map((item) => (
            <Can key={item.key} I={item.action} a={item.subject}>
              <SidebarItem
                item={item}
                collapsed={collapsed}
                onSidebarToggle={() => {
                  closeMobileSidebar();
                  handleDesktopToggle();
                }}
              />
            </Can>
          ))}
        </div>
      </aside>
    </>
  );
};
